import { MonetaryInput } from "$root/components/monetary-input";
import { FormFields } from "$root/ui-lib/form/FormFields";
import { formatCurrencyAmount } from "$root/utils/currency";
import type { Currencies, CustomAttributeValue } from "@eu.mdotm.api/sphere";
import { CircularProgressBar, Text } from "@mdotm/shadcn/legacy/components";
import { FormField } from "@mdotm/shadcn/mdotm/components";
import type { Control, FormState, Path } from "react-hook-form";
import { isAumSystemAttribute } from "./aum";
import { MAX_AUM } from "./aumCap";
import type { CustomAttributeDto } from "./type";

/** The slice of a form this field binds to: the investment's custom-attribute values. */
type AumFormValues = { customAttributes: Array<CustomAttributeValue | undefined> };

export type AumFieldProps<T extends AumFormValues> = {
	/** The tenant's custom attributes; the AUM one is picked out by its system key. */
	availableAttributes: CustomAttributeDto[];
	/** The form's current `customAttributes` — the AUM entry's index in it is the field path. */
	values: T["customAttributes"] | undefined;
	control: Control<T>;
	formState: FormState<T>;
	/** Editable input when true, formatted read-only value otherwise. */
	edit: boolean;
	/** Currency the AUM is expressed in; its symbol adorns both the input and the read-only value. */
	currency: Currencies | null | undefined;
	/** Drops the "Optional" mark. The form's schema owns the rule that makes the AUM required. */
	required?: boolean;
};

/**
 * The portfolio AUM as a portfolio attribute. The value is the system-managed AUM custom attribute,
 * so the edit binds to that attribute's entry in the form's `customAttributes` and saves with the
 * rest of them. Renders nothing when the tenant has no AUM attribute, and a loading indicator in
 * edit mode until the form holds an entry for it.
 */
export function AumField<T extends AumFormValues>({
	availableAttributes,
	values,
	control,
	formState,
	edit,
	currency,
	required = false,
}: AumFieldProps<T>): React.JSX.Element | null {
	const aumAttribute = availableAttributes.find(isAumSystemAttribute);
	if (!aumAttribute) {
		return null;
	}
	const label = aumAttribute.name ?? "AUM";
	const index = (values ?? []).findIndex((value) => value?.attributeUuid === aumAttribute.attributeUuid);

	if (!edit) {
		return (
			<FormField label={label} className="mb-4 max-w-[470px]">
				{formatCurrencyAmount(values?.[index]?.numberValue, currency)}
			</FormField>
		);
	}

	if (index < 0) {
		return (
			<FormField label={label} className="mb-4 max-w-[470px]">
				<CircularProgressBar style={{ width: "1rem" }} value="indeterminate" />
			</FormField>
		);
	}

	return (
		<div className="relative mb-4 max-w-[470px]">
			<FormFields.Custom
				control={control}
				formState={formState}
				// `T` extends AumFormValues, so this path (and its numeric value) exists on every form the field accepts.
				name={`customAttributes.${index}.numberValue` as Path<T>}
				label={label}
			>
				{({ id, invalid, field }) => (
					<MonetaryInput
						id={id}
						invalid={invalid}
						value={(field.value as CustomAttributeValue["numberValue"]) ?? null}
						onChange={field.onChange}
						onBlur={field.onBlur}
						max={MAX_AUM}
						currency={currency}
						data-qualifier="portfolioWizard/mainInfo/aum"
					/>
				)}
			</FormFields.Custom>
			{!required && (
				<div className="absolute right-0 top-0">
					<Text type="Body/M/Book" as="p" className="text-right">
						Optional
					</Text>
				</div>
			)}
		</div>
	);
}
